/**
 * Static mock consultant profiles for the card UI.
 * Keyed by slug — maps to real photos from /assets/.
 */

import kevinPhoto from '../../../../assets/kevin.jpg';
import karanPhoto from '../../../../assets/karan.jpg';
import prashantPhoto from '../../../../assets/prashant_katyan.jpg';
import rudraPhoto from '../../../../assets/rudra_pratap_Singh.jpg';
import ujjvalSihagPhoto from '../../../../assets/ujjval-sihag.jpg';
import dakshPhoto from '../../../../assets/daksh.jpg';
import dikhitPhoto from '../../../../assets/dikhit.jpg';

export interface ConsultantMock {
  slug: string;
  photo: string;
  title: string;
  bio: string;
  experience: string;
  sessionDuration: string;
  badge?: 'featured' | 'free' | 'new';
}

export const CONSULTANT_MOCK_DATA: Record<string, ConsultantMock> = {
  kevin: {
    slug: 'kevin',
    photo: kevinPhoto,
    title: 'Senior Immigration Consultant',
    bio: 'Kevin is a Regulated Canadian Immigration Consultant (RCIC) with over 10 years of experience helping clients achieve permanent residency, work permits, and citizenship. He specialises in complex cases, appeals, and comprehensive strategy planning for long-term settlement in Canada.',
    experience: '10+ years',
    sessionDuration: '45 minutes',
    badge: 'featured',
  },
  karan: {
    slug: 'karan',
    photo: karanPhoto,
    title: 'PR & Family Sponsorship Expert',
    bio: 'Karan has helped hundreds of families reunite in Canada through spousal, parental, and dependent sponsorship programs. His deep knowledge of PR pathways ensures smooth, stress-free applications, allowing families to focus on building their new lives together.',
    experience: '7+ years',
    sessionDuration: '45 minutes',
  },
  prashant: {
    slug: 'prashant',
    photo: prashantPhoto,
    title: 'Legal & Immigration Specialist',
    bio: 'Adv Prashant Katyan specialises in legal cases, immigration matters, and traffic ticket disputes. Based in New Delhi, he helps clients navigate complex legal processes with clear, practical guidance tailored to each situation.',
    experience: '6+ years',
    sessionDuration: '30 minutes',
  },
  rudra: {
    slug: 'rudra',
    photo: rudraPhoto,
    title: 'Legal & Immigration Advocate',
    bio: 'Adv Rudra Pratap Singh brings deep expertise in criminal cases, matrimonial disputes, civil conflicts, and immigration matters. Operating from Delhi, he provides strategic legal counsel and hands-on support across a wide range of client needs.',
    experience: '8+ years',
    sessionDuration: '45 minutes',
  },
  ujjval: {
    slug: 'ujjval',
    photo: ujjvalSihagPhoto,
    title: 'Work Permit & LMIA Specialist',
    bio: 'Ujjval Sihag works closely with employers and foreign workers to navigate the Labour Market Impact Assessment (LMIA) process. His expertise ensures businesses remain fully compliant while workers transition smoothly into the Canadian workforce.',
    experience: '5+ years',
    sessionDuration: '45 minutes',
  },
  sihag: {
    slug: 'sihag',
    photo: ujjvalSihagPhoto,
    title: 'Work Permit & LMIA Specialist',
    bio: 'Ujjval Sihag works closely with employers and foreign workers to navigate the Labour Market Impact Assessment (LMIA) process. His expertise ensures businesses remain fully compliant while workers transition smoothly into the Canadian workforce.',
    experience: '5+ years',
    sessionDuration: '45 minutes',
  },
  daksh: {
    slug: 'daksh',
    photo: dakshPhoto,
    title: 'Tax & Immigration Advisor',
    bio: 'Daksh brings a rare combination of immigration law knowledge and Canadian tax expertise. He helps new arrivals understand their tax obligations, cross-border asset transfers, and long-term financial planning as they settle and grow roots in Canada.',
    experience: '6+ years',
    sessionDuration: '30 minutes',
    badge: 'new',
  },
  dikhit: {
    slug: 'dikhit',
    photo: dikhitPhoto,
    title: 'Study Visa & Work Permit Consultant',
    bio: 'Dikhit assists students transitioning from study permits to post-graduate work permits (PGWP) and open work permits. He provides clear, practical guidance at every stage of the immigration journey to prevent status expiration.',
    experience: '5+ years',
    sessionDuration: '30 minutes',
  },
};

/**
 * Look up mock data by slug, with two fallback strategies:
 * 1. Exact lowercase slug match (e.g. "kevin" → kevin)
 * 2. "starts-with" partial match (e.g. "sihag_thakur" → sihag)
 * 3. Caller can also pass the displayName — we check first-name match
 */
export function getConsultantMock(
  slug: string | null | undefined,
  displayName?: string,
): ConsultantMock | null {
  if (!slug && !displayName) return null;

  // 1. Exact slug match
  if (slug) {
    const key = slug.toLowerCase();
    if (CONSULTANT_MOCK_DATA[key]) return CONSULTANT_MOCK_DATA[key];

    // 2. Partial / starts-with slug match (handles "sihag_thakur" → "sihag")
    const partialKey = Object.keys(CONSULTANT_MOCK_DATA).find(
      (k) => key.startsWith(k) || key.includes(k),
    );
    if (partialKey) return CONSULTANT_MOCK_DATA[partialKey];
  }

  // 3. Match by first word of displayName (e.g. "Daksh Arora" → "daksh")
  if (displayName) {
    const firstName = displayName.trim().split(/\s+/)[0]?.toLowerCase();
    if (firstName && CONSULTANT_MOCK_DATA[firstName]) return CONSULTANT_MOCK_DATA[firstName];

    // 4. Match by last name (e.g. "Ujjval Sihag" → "sihag")
    const parts = displayName.trim().split(/\s+/);
    const lastName = parts.length > 1 ? parts[parts.length - 1]?.toLowerCase() : '';
    if (lastName && CONSULTANT_MOCK_DATA[lastName]) return CONSULTANT_MOCK_DATA[lastName];
  }

  return null;
}
